import { Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import { environment } from 'src/app/environments/environments';
import { NotificationService } from 'src/app/shared/services/notification.service';


@Injectable({
  providedIn: 'root'
})
export class EncryptionService {
  // Use a strong secret key - in production, this should come from environment variables
  private readonly SECRET_KEY = environment.localStorage.secretKey;

  constructor(private notificationService: NotificationService) {}

  /**
   * Encrypt data before storing in localStorage
   */
  encrypt(data: any): string {
    try {
      const jsonString = JSON.stringify(data);
      const encrypted = CryptoJS.AES.encrypt(jsonString, this.SECRET_KEY).toString();
      return encrypted;
    } catch (error) {
      this.notificationService.showError('Failed to encrypt data');
      throw new Error('Failed to encrypt data');
    }
  }

  /**
   * Decrypt data retrieved from localStorage
   */
  decrypt(encryptedData: string): any {
    try {
      const decrypted = CryptoJS.AES.decrypt(encryptedData, this.SECRET_KEY);
      const jsonString = decrypted.toString(CryptoJS.enc.Utf8);
      
      if (!jsonString) {
        throw new Error('Decryption resulted in empty string');
      }
      
      return JSON.parse(jsonString);
    } catch (error) {
      this.notificationService.showError('Failed to decrypt data');
      throw new Error('Failed to decrypt data');
    }
  }

  /**
   * Set encrypted item in localStorage
   */
  setItem(key: string, value: any): void {
    try {
      const encrypted = this.encrypt(value);
      localStorage.setItem(key, encrypted);
    } catch (error) {
      this.notificationService.showError('Failed to save data');
    }
  }

  /**
   * Get and decrypt item from localStorage
   */
  getItem<T = any>(key: string): T | null {
    try {
      const encrypted = localStorage.getItem(key);
      if (!encrypted) {
        return null;
      }
      return this.decrypt(encrypted) as T;
    } catch (error) {
      this.notificationService.showError('Failed to read saved data');
      // Clear corrupted data
      localStorage.removeItem(key);
      return null;
    }
  }

  /**
   * Remove item from localStorage
   */
  removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      this.notificationService.showError('Failed to remove saved data');
    }
  }

  /**
   * Clear all localStorage
   */
  clear(): void {
    try {
      localStorage.clear();
    } catch (error) {
      this.notificationService.showError('Failed to clear saved data');
    }
  }

  /**
   * Check if item exists in localStorage
   */
  hasItem(key: string): boolean {
    return localStorage.getItem(key) !== null;
  }
}

